/**
 * 2026-08 — Vertragsvorbereitungs-Modul.
 *
 * Konkrete Vertrags-Entwürfe: Anlage aus Vertragsvorlage per Wizard,
 * freie Bearbeitung des Textes, PDF-Export mit Unterschriftsblöcken auf
 * Vertragspapier, Statuswechsel Entwurf → bereit → unterzeichnet → aktiv,
 * automatische Anlage der Ziel-Entität (Tenancy oder Verwaltervertrag).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useConfirm } from "@/components/ConfirmDialog";
import {
  Briefcase, Plus, MagnifyingGlass, PencilSimple, Trash, FileText, FilePdf,
  CheckCircle, Lightning, X, ArrowRight, ArrowLeft, Signature, BookOpen,
  ArrowsClockwise, Star,
} from "@phosphor-icons/react";

const KINDS = [
  { value: "wohnraum",    label: "Wohnraum-Mietvertrag", target: "Mietverhältnis" },
  { value: "gewerbe",     label: "Gewerbe-Mietvertrag",  target: "Mietverhältnis" },
  { value: "staffelmiet", label: "Staffelmietvertrag",   target: "Mietverhältnis" },
  { value: "indexmiet",   label: "Indexmietvertrag",     target: "Mietverhältnis" },
  { value: "stellplatz",  label: "Stellplatz-Vertrag",   target: "Mietverhältnis" },
  { value: "garage",      label: "Garagenvertrag",       target: "Mietverhältnis" },
  { value: "verwalter",   label: "Verwaltervertrag",     target: "Verwaltervertrag" },
  { value: "sonstige",    label: "Sonstiger Vertrag",    target: "—" },
];
const kindMeta = (k) => KINDS.find((x) => x.value === k) || KINDS[0];

const STATUS_META = {
  draft:     { label: "Entwurf",       tone: "bg-slate-100 text-slate-700" },
  ready:     { label: "Versandbereit", tone: "bg-sky-50 text-sky-700" },
  signed:    { label: "Unterzeichnet", tone: "bg-amber-50 text-amber-800" },
  activated: { label: "Aktiv",         tone: "bg-emerald-50 text-emerald-700" },
  cancelled: { label: "Storniert",     tone: "bg-rose-50 text-rose-700" },
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "2-digit" });
  } catch { return iso; }
};

export default function Contracts() {
  const confirmDialog = useConfirm();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [activeKind, setActiveKind] = useState(null);
  const [activeStatus, setActiveStatus] = useState(null);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [detailId, setDetailId] = useState(null);

  const reload = async () => {
    try {
      const { data } = await api.get("/contracts");
      setItems(data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { reload(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((it) => {
      if (activeKind && it.kind !== activeKind) return false;
      if (activeStatus && it.status !== activeStatus) return false;
      if (!q) return true;
      const hay = [
        it.title, it.contract_number, it.property_name, it.unit_label,
        ...(it.tenant_names || []),
      ].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [items, search, activeKind, activeStatus]);

  const remove = async (rec) => {
    const ok = await confirmDialog({
      title: "Vertragsentwurf löschen?",
      description: `„${rec.title}" wird endgültig entfernt.`,
      confirmLabel: "Löschen", tone: "danger",
    });
    if (!ok) return;
    try {
      await api.delete(`/contracts/${rec.id}`);
      toast.success("Gelöscht");
      reload();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="p-6 space-y-4" data-testid="contracts-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900 flex items-center gap-2">
            <Briefcase weight="duotone" className="text-emerald-600" size={26} />
            Vertragsvorbereitung
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Konkrete Verträge aus Vorlagen erstellen, prüfen, unterschreiben lassen und aktivieren.
          </p>
        </div>
        <Button
          onClick={() => setWizardOpen(true)}
          className="bg-slate-900 hover:bg-slate-800 gap-2"
          data-testid="new-contract-btn"
        >
          <Plus size={16} /> Neuer Vertrag
        </Button>
      </div>

      {/* Filter row */}
      <div className="flex items-center gap-2 flex-wrap bg-white p-3 border border-slate-200 rounded-md">
        <div className="relative w-72">
          <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Suchen (Titel, Mieter, Objekt …)"
            className="pl-8 h-9 text-sm"
            data-testid="contracts-search"
          />
        </div>
        <select
          value={activeKind || ""} onChange={(e) => setActiveKind(e.target.value || null)}
          className="h-9 border border-slate-200 bg-white rounded-sm text-sm px-2"
          data-testid="filter-kind"
        >
          <option value="">Alle Vertragstypen</option>
          {KINDS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
        </select>
        <select
          value={activeStatus || ""} onChange={(e) => setActiveStatus(e.target.value || null)}
          className="h-9 border border-slate-200 bg-white rounded-sm text-sm px-2"
          data-testid="filter-status"
        >
          <option value="">Alle Status</option>
          {Object.entries(STATUS_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <span className="text-xs text-slate-500 ml-auto">{filtered.length} Verträge</span>
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <table className="w-full text-sm" data-testid="contracts-table">
          <thead className="bg-slate-50 border-b border-slate-200 text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
            <tr>
              <th className="text-left px-4 py-2 w-[26%]">Titel</th>
              <th className="text-left px-3 py-2 w-[14%]">Typ</th>
              <th className="text-left px-3 py-2 w-[22%]">Mieter / Auftraggeber</th>
              <th className="text-left px-3 py-2 w-[18%]">Objekt / Einheit</th>
              <th className="text-left px-3 py-2 w-[10%]">Status</th>
              <th className="text-left px-3 py-2 w-[8%]">Geändert</th>
              <th className="w-[2%]"></th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={7} className="py-8 text-center text-slate-500">Lade…</td></tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={7} className="py-10 text-center text-slate-500">
                <Briefcase size={32} className="mx-auto text-slate-300 mb-2" />
                Keine Verträge vorhanden. Legen Sie über &quot;Neuer Vertrag&quot; den ersten Vertragsentwurf an.
              </td></tr>
            )}
            {filtered.map((it) => {
              const km = kindMeta(it.kind);
              const sm = STATUS_META[it.status] || STATUS_META.draft;
              return (
                <tr
                  key={it.id}
                  onClick={() => setDetailId(it.id)}
                  className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors"
                  data-testid={`contract-row-${it.id}`}
                >
                  <td className="px-4 py-2">
                    <div className="font-medium text-slate-900 truncate">{it.title}</div>
                    {it.contract_number && (
                      <div className="text-[11px] text-slate-500 tabular-nums">{it.contract_number}</div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-slate-700">{km.label}</td>
                  <td className="px-3 py-2 text-slate-700">
                    {(it.tenant_names || []).join(", ") || "—"}
                  </td>
                  <td className="px-3 py-2 text-slate-700">
                    {it.property_name ? `${it.property_name}${it.unit_label ? " · " + it.unit_label : ""}` : "—"}
                  </td>
                  <td className="px-3 py-2">
                    <span className={`inline-block px-2 py-0.5 rounded-sm text-[11px] font-medium ${sm.tone}`}>
                      {sm.label}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-[11px] text-slate-500 tabular-nums">
                    {fmtDate(it.updated_at || it.created_at)}
                  </td>
                  <td className="px-2 py-2 text-right" onClick={(e) => e.stopPropagation()}>
                    {it.status !== "activated" && (
                      <button
                        onClick={() => remove(it)}
                        className="text-slate-400 hover:text-rose-600 p-1"
                        data-testid={`contract-delete-${it.id}`}
                        title="Löschen"
                      >
                        <Trash size={14} />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* New Contract Wizard */}
      {wizardOpen && (
        <NewContractWizard
          onClose={() => setWizardOpen(false)}
          onCreated={(id) => { setWizardOpen(false); reload(); setDetailId(id); }}
        />
      )}

      {/* Detail Dialog */}
      {detailId && (
        <ContractDetailDialog
          id={detailId}
          onClose={() => setDetailId(null)}
          onChanged={() => reload()}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Wizard: Kind → Template → Parties → Wizard-Data → Review
// ---------------------------------------------------------------------------
function NewContractWizard({ onClose, onCreated }) {
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [properties, setProperties] = useState([]);
  const [units, setUnits] = useState([]);
  const [contacts, setContacts] = useState([]);

  const [form, setForm] = useState({
    title: "",
    kind: "wohnraum",
    template_id: "",
    property_id: "",
    unit_id: "",
    additional_unit_ids: [],
    owner_contact_id: "",
    tenant_contact_ids: [],
    wizard_data: {
      contract_start: "",
      contract_end: "",
      rent_cold: "",
      utilities_advance: "",
      heating_advance: "",
      deposit: "",
      persons_count: "",
      termination_notice_months: 3,
      due_day: 3,
      payment_rhythm: "monthly",
      // Verwaltervertrag
      mgmt_type: "miet",
      fee_amount: "",
      fee_interval: "monthly",
      is_net: false,
      vat_rate: 19,
      scope_description: "",
      // Indexmiete
      index_base: "",
      index_base_date: "",
      // Staffelmiete
      staffel_steps_json: "",
      // Gewerbe VAT
      commercial_vat_rate: "",
    },
    signatories: [],
  });

  useEffect(() => {
    api.get("/contract-templates").then((r) => setTemplates(r.data || []));
    api.get("/properties").then((r) => setProperties(r.data || []));
    api.get("/contacts", { params: { limit: 1000 } }).then((r) => setContacts(r.data || []));
  }, []);

  useEffect(() => {
    if (form.property_id) {
      api.get("/properties/units/all", { params: { property_id: form.property_id } })
        .then((r) => setUnits(r.data || []));
    } else {
      setUnits([]);
    }
  }, [form.property_id]);

  const isMgmt = form.kind === "verwalter";
  const isMisc = form.kind === "sonstige";
  const needsUnit = !isMgmt && !isMisc;

  const step1Valid = form.title.trim() && form.kind;
  const step2Valid = true; // Template optional
  const step3Valid = isMgmt
    ? !!form.owner_contact_id
    : isMisc
      ? true
      : (form.property_id && form.unit_id && form.tenant_contact_ids.length > 0);
  const step4Valid = !!form.wizard_data.contract_start;

  const setWizard = (patch) => setForm((f) => ({ ...f, wizard_data: { ...f.wizard_data, ...patch } }));
  const setField = (patch) => setForm((f) => ({ ...f, ...patch }));

  const create = async () => {
    setBusy(true);
    try {
      // Auto-populate signatory blocks from contacts
      const signatories = [];
      const findContact = (id) => contacts.find((c) => c.id === id);
      if (isMgmt) {
        const owner = findContact(form.owner_contact_id);
        if (owner) {
          signatories.push({
            role: "Auftraggeber",
            name: owner.company_name || `${owner.first_name || ""} ${owner.last_name || ""}`.trim(),
            contact_id: owner.id,
          });
        }
        signatories.push({ role: "Verwalter", name: "" });
      } else if (!isMisc) {
        // Vermieter = Objekt-Eigentümer, Mieter = tenant_contact_ids
        const prop = properties.find((p) => p.id === form.property_id);
        if (prop?.owner_contact_id) {
          const own = findContact(prop.owner_contact_id);
          signatories.push({
            role: "Vermieter",
            name: own?.company_name || `${own?.first_name || ""} ${own?.last_name || ""}`.trim() || "",
            contact_id: own?.id,
          });
        }
        form.tenant_contact_ids.forEach((tid) => {
          const t = findContact(tid);
          if (t) {
            signatories.push({
              role: "Mieter",
              name: t.company_name || `${t.first_name || ""} ${t.last_name || ""}`.trim(),
              contact_id: t.id,
            });
          }
        });
      }

      const payload = {
        title: form.title.trim(),
        kind: form.kind,
        template_id: form.template_id || null,
        property_id: needsUnit ? form.property_id : null,
        unit_id: needsUnit ? form.unit_id : null,
        additional_unit_ids: needsUnit ? form.additional_unit_ids : [],
        owner_contact_id: isMgmt ? form.owner_contact_id : null,
        tenant_contact_ids: needsUnit ? form.tenant_contact_ids : [],
        wizard_data: form.wizard_data,
        signatories,
        status: "draft",
      };
      const { data } = await api.post("/contracts", payload);
      toast.success("Vertragsentwurf angelegt");
      onCreated(data.id);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const filteredTemplates = templates.filter((t) => {
    // wohnraum,gewerbe,stellplatz,garage,sonstige exist; staffel/index/mgmt map to wohnraum/sonstige
    if (form.kind === "wohnraum" || form.kind === "staffelmiet" || form.kind === "indexmiet") {
      return t.contract_type === "wohnraum";
    }
    if (form.kind === "gewerbe") return t.contract_type === "gewerbe";
    if (form.kind === "stellplatz") return t.contract_type === "stellplatz";
    if (form.kind === "garage") return t.contract_type === "garage";
    return true;
  });

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl" data-testid="new-contract-wizard">
        <DialogHeader>
          <DialogTitle>Neuen Vertrag anlegen — Schritt {step} von 4</DialogTitle>
          <DialogDescription>
            {step === 1 && "Titel und Vertragstyp festlegen."}
            {step === 2 && "Optional: Vertragsvorlage als Startpunkt wählen."}
            {step === 3 && (isMgmt ? "Auftraggeber und Objekt-Zuordnung." : "Objekt, Einheit und Mieter zuordnen.")}
            {step === 4 && "Vertragskonditionen und Signaturen."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2 max-h-[60vh] overflow-y-auto pr-2">
          {step === 1 && (
            <div className="space-y-3">
              <div>
                <Label>Titel*</Label>
                <Input
                  value={form.title} onChange={(e) => setField({ title: e.target.value })}
                  placeholder="z.B. Mietvertrag Wohnung 3.OG rechts"
                  data-testid="wizard-title"
                />
              </div>
              <div>
                <Label>Vertragstyp*</Label>
                <div className="grid grid-cols-2 gap-2 mt-1">
                  {KINDS.map((k) => (
                    <button
                      key={k.value}
                      onClick={() => setField({ kind: k.value })}
                      className={`text-left border rounded-sm px-3 py-2 transition-colors ${
                        form.kind === k.value
                          ? "border-emerald-600 bg-emerald-50/50"
                          : "border-slate-200 hover:border-slate-300"
                      }`}
                      data-testid={`wizard-kind-${k.value}`}
                    >
                      <div className="text-sm font-medium text-slate-900">{k.label}</div>
                      <div className="text-[11px] text-slate-500">→ {k.target}</div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-2">
              <Label>Vertragsvorlage (optional)</Label>
              <div className="text-xs text-slate-500 mb-2">
                Die Vorlage liefert die Textbausteine; Sie können den Vertrag danach frei bearbeiten.
              </div>
              <div className="space-y-1 max-h-72 overflow-y-auto border border-slate-200 rounded-sm bg-white p-1">
                <button
                  onClick={() => setField({ template_id: "" })}
                  className={`w-full text-left px-3 py-2 rounded-sm text-sm ${
                    !form.template_id ? "bg-emerald-50 border border-emerald-200" : "hover:bg-slate-50"
                  }`}
                  data-testid="wizard-template-none"
                >
                  <div className="font-medium">Ohne Vorlage — freier Text</div>
                  <div className="text-[11px] text-slate-500">Vertrag manuell verfassen</div>
                </button>
                {filteredTemplates.map((tpl) => (
                  <button
                    key={tpl.id}
                    onClick={() => setField({ template_id: tpl.id })}
                    className={`w-full text-left px-3 py-2 rounded-sm text-sm ${
                      form.template_id === tpl.id ? "bg-emerald-50 border border-emerald-200" : "hover:bg-slate-50"
                    }`}
                    data-testid={`wizard-template-${tpl.id}`}
                  >
                    <div className="font-medium">{tpl.name}</div>
                    <div className="text-[11px] text-slate-500">
                      {tpl.contract_type} · {tpl.enabled_count || 0}/{tpl.block_count || 0} Bausteine
                    </div>
                  </button>
                ))}
                {filteredTemplates.length === 0 && (
                  <div className="text-xs text-slate-400 p-3 italic">
                    Keine passenden Vorlagen für {kindMeta(form.kind).label}.
                  </div>
                )}
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-3">
              {isMgmt && (
                <div>
                  <Label>Auftraggeber (Eigentümer)*</Label>
                  <select
                    value={form.owner_contact_id}
                    onChange={(e) => setField({ owner_contact_id: e.target.value })}
                    className="w-full h-9 border border-slate-200 rounded-sm px-2 text-sm"
                    data-testid="wizard-owner"
                  >
                    <option value="">— wählen —</option>
                    {contacts.filter((c) => (c.roles || []).includes("owner")).map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.company_name || `${c.first_name || ""} ${c.last_name || ""}`.trim()}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              {needsUnit && (
                <>
                  <div>
                    <Label>Objekt*</Label>
                    <select
                      value={form.property_id}
                      onChange={(e) => setField({ property_id: e.target.value, unit_id: "" })}
                      className="w-full h-9 border border-slate-200 rounded-sm px-2 text-sm"
                      data-testid="wizard-property"
                    >
                      <option value="">— wählen —</option>
                      {properties.map((p) => (
                        <option key={p.id} value={p.id}>{p.name || p.address}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label>Einheit*</Label>
                    <select
                      value={form.unit_id}
                      onChange={(e) => setField({ unit_id: e.target.value })}
                      disabled={!form.property_id}
                      className="w-full h-9 border border-slate-200 rounded-sm px-2 text-sm disabled:opacity-50"
                      data-testid="wizard-unit"
                    >
                      <option value="">— wählen —</option>
                      {units.map((u) => (
                        <option key={u.id} value={u.id}>
                          {u.unit_number}{u.floor ? ` · ${u.floor}` : ""}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label>Mieter* (mehrere möglich – Strg-Klick / Cmd-Klick)</Label>
                    <select
                      multiple
                      value={form.tenant_contact_ids}
                      onChange={(e) => setField({
                        tenant_contact_ids: Array.from(e.target.selectedOptions).map((o) => o.value),
                      })}
                      className="w-full min-h-[100px] border border-slate-200 rounded-sm px-2 py-1 text-sm"
                      data-testid="wizard-tenants"
                    >
                      {contacts.filter((c) => (c.roles || []).includes("tenant") || (c.roles || []).length === 0).map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.company_name || `${c.first_name || ""} ${c.last_name || ""}`.trim()}
                        </option>
                      ))}
                    </select>
                  </div>
                </>
              )}
              {isMisc && (
                <div className="text-sm text-slate-600 bg-slate-50 border border-slate-200 rounded-sm p-3">
                  Bei sonstigen Verträgen ist keine feste Zuordnung erforderlich. Der Vertrag kann
                  nach Unterzeichnung nicht automatisch als Mietvertrag / Verwaltervertrag
                  aktiviert werden — er dient nur der PDF-Erstellung.
                </div>
              )}
            </div>
          )}

          {step === 4 && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Vertragsbeginn*</Label>
                  <Input
                    type="date" value={form.wizard_data.contract_start}
                    onChange={(e) => setWizard({ contract_start: e.target.value })}
                    data-testid="wizard-contract-start"
                  />
                </div>
                <div>
                  <Label>Vertragsende (leer = unbefristet)</Label>
                  <Input
                    type="date" value={form.wizard_data.contract_end}
                    onChange={(e) => setWizard({ contract_end: e.target.value })}
                    data-testid="wizard-contract-end"
                  />
                </div>
              </div>

              {isMgmt ? (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Verwaltungsart</Label>
                      <select
                        value={form.wizard_data.mgmt_type}
                        onChange={(e) => setWizard({ mgmt_type: e.target.value })}
                        className="w-full h-9 border border-slate-200 rounded-sm px-2 text-sm"
                      >
                        <option value="miet">Mietverwaltung</option>
                        <option value="weg">WEG-Verwaltung</option>
                        <option value="sondereigentum">Sondereigentumsverwaltung</option>
                        <option value="gewerbe">Gewerbeverwaltung</option>
                        <option value="sonstige">Sonstige</option>
                      </select>
                    </div>
                    <div>
                      <Label>Honorar (EUR)</Label>
                      <Input
                        type="number" step="0.01" value={form.wizard_data.fee_amount}
                        onChange={(e) => setWizard({ fee_amount: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <Label>Intervall</Label>
                      <select
                        value={form.wizard_data.fee_interval}
                        onChange={(e) => setWizard({ fee_interval: e.target.value })}
                        className="w-full h-9 border border-slate-200 rounded-sm px-2 text-sm"
                      >
                        <option value="monthly">Monatlich</option>
                        <option value="quarterly">Quartalsweise</option>
                        <option value="yearly">Jährlich</option>
                      </select>
                    </div>
                    <div className="flex items-end gap-2">
                      <label className="flex items-center gap-2 text-sm text-slate-700">
                        <input
                          type="checkbox" checked={form.wizard_data.is_net}
                          onChange={(e) => setWizard({ is_net: e.target.checked })}
                          className="w-4 h-4 accent-emerald-600"
                        />
                        Netto
                      </label>
                    </div>
                    <div>
                      <Label>USt %</Label>
                      <Input
                        type="number" step="0.5" value={form.wizard_data.vat_rate}
                        onChange={(e) => setWizard({ vat_rate: e.target.value })}
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Umfang der Verwaltungstätigkeit</Label>
                    <Textarea
                      rows={3}
                      value={form.wizard_data.scope_description}
                      onChange={(e) => setWizard({ scope_description: e.target.value })}
                    />
                  </div>
                </>
              ) : (
                <>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <Label>Kaltmiete (EUR)</Label>
                      <Input
                        type="number" step="0.01" value={form.wizard_data.rent_cold}
                        onChange={(e) => setWizard({ rent_cold: e.target.value })}
                        data-testid="wizard-rent-cold"
                      />
                    </div>
                    <div>
                      <Label>Nebenkosten (EUR)</Label>
                      <Input
                        type="number" step="0.01" value={form.wizard_data.utilities_advance}
                        onChange={(e) => setWizard({ utilities_advance: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label>Heizkosten (EUR)</Label>
                      <Input
                        type="number" step="0.01" value={form.wizard_data.heating_advance}
                        onChange={(e) => setWizard({ heating_advance: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <Label>Kaution (EUR)</Label>
                      <Input
                        type="number" step="0.01" value={form.wizard_data.deposit}
                        onChange={(e) => setWizard({ deposit: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label>Personen</Label>
                      <Input
                        type="number" value={form.wizard_data.persons_count}
                        onChange={(e) => setWizard({ persons_count: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label>Kündigungsfrist (Monate)</Label>
                      <Input
                        type="number" value={form.wizard_data.termination_notice_months}
                        onChange={(e) => setWizard({ termination_notice_months: e.target.value })}
                      />
                    </div>
                  </div>
                  {form.kind === "gewerbe" && (
                    <div>
                      <Label>Umsatzsteuer (%)</Label>
                      <Input
                        type="number" step="0.5" value={form.wizard_data.vat_rate}
                        onChange={(e) => setWizard({ vat_rate: e.target.value })}
                      />
                    </div>
                  )}
                  {form.kind === "indexmiet" && (
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label>VPI-Basiswert</Label>
                        <Input
                          type="number" step="0.01" value={form.wizard_data.index_base}
                          onChange={(e) => setWizard({ index_base: e.target.value })}
                        />
                        <div className="text-[11px] text-slate-500 mt-1">
                          Leer lassen → wird automatisch aus dem Vertragsbeginn abgeleitet
                        </div>
                      </div>
                      <div>
                        <Label>VPI-Basisdatum</Label>
                        <Input
                          type="date" value={form.wizard_data.index_base_date}
                          onChange={(e) => setWizard({ index_base_date: e.target.value })}
                        />
                      </div>
                    </div>
                  )}
                  {form.kind === "staffelmiet" && (
                    <div>
                      <Label>Staffelstufen (JSON-Array)</Label>
                      <Textarea
                        rows={4}
                        placeholder='[{"date":"2027-01-01","rent_cold":950}, {"date":"2028-01-01","rent_cold":1000}]'
                        value={form.wizard_data.staffel_steps_json}
                        onChange={(e) => setWizard({ staffel_steps_json: e.target.value })}
                        className="font-mono text-xs"
                      />
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="justify-between">
          <Button variant="outline" onClick={step > 1 ? () => setStep(step - 1) : onClose}
                  data-testid="wizard-back">
            {step > 1 ? <><ArrowLeft size={14} /> Zurück</> : "Abbrechen"}
          </Button>
          {step < 4 ? (
            <Button
              onClick={() => setStep(step + 1)}
              disabled={(step === 1 && !step1Valid) || (step === 2 && !step2Valid) || (step === 3 && !step3Valid)}
              className="bg-slate-900 hover:bg-slate-800 gap-2"
              data-testid="wizard-next"
            >
              Weiter <ArrowRight size={14} />
            </Button>
          ) : (
            <Button
              onClick={create} disabled={busy || !step4Valid}
              className="bg-emerald-600 hover:bg-emerald-700 gap-2"
              data-testid="wizard-create"
            >
              {busy ? "Anlegen…" : "Vertragsentwurf anlegen"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Detail Dialog with edit/regenerate/pdf/mark-signed/activate
// ---------------------------------------------------------------------------
function ContractDetailDialog({ id, onClose, onChanged }) {
  const confirmDialog = useConfirm();
  const [rec, setRec] = useState(null);
  const [busy, setBusy] = useState(false);
  const [body, setBody] = useState("");
  const [dirty, setDirty] = useState(false);
  const [signPlace, setSignPlace] = useState("");
  const [signDate, setSignDate] = useState("");
  const [showClauses, setShowClauses] = useState(false);
  const bodyRef = useRef(null);

  const insertClause = (clauseHtml) => {
    const el = bodyRef.current;
    if (!el) {
      setBody((b) => (b || "") + "\n\n" + clauseHtml);
      setDirty(true);
      return;
    }
    const start = el.selectionStart ?? body.length;
    const end = el.selectionEnd ?? body.length;
    const next = body.slice(0, start) + "\n\n" + clauseHtml + "\n\n" + body.slice(end);
    setBody(next);
    setDirty(true);
    // Refocus + move cursor to end of inserted block
    setTimeout(() => {
      el.focus();
      const pos = start + clauseHtml.length + 4;
      el.setSelectionRange(pos, pos);
    }, 30);
  };

  const load = async () => {
    try {
      const { data } = await api.get(`/contracts/${id}`);
      setRec(data);
      setBody(data.body_html || "");
      setSignPlace(data.signature_place || "");
      setSignDate(data.signature_date || "");
      setDirty(false);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, [id]);

  if (!rec) {
    return (
      <Dialog open onOpenChange={onClose}>
        <DialogContent className="max-w-4xl">
          <DialogHeader><DialogTitle>Lade…</DialogTitle></DialogHeader>
        </DialogContent>
      </Dialog>
    );
  }

  const km = kindMeta(rec.kind);
  const sm = STATUS_META[rec.status] || STATUS_META.draft;
  const editable = rec.status !== "activated" && rec.status !== "cancelled";

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/contracts/${id}`, {
        ...rec,
        body_html: body,
        signature_place: signPlace || null,
        signature_date: signDate || null,
      });
      toast.success("Gespeichert");
      setDirty(false);
      onChanged();
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const regenerate = async () => {
    if (!rec.template_id) {
      toast.error("Keine Vorlage verknüpft.");
      return;
    }
    const ok = await confirmDialog({
      title: "Text neu aus Vorlage generieren?",
      description: "Ihr aktueller Vertragstext wird überschrieben.",
      confirmLabel: "Neu generieren", tone: "warn",
    });
    if (!ok) return;
    try {
      const { data } = await api.post(`/contracts/${id}/render`);
      setBody(data.body_html || "");
      setDirty(true);
      toast.success("Vorlage neu geladen");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const downloadPdf = async () => {
    try {
      const resp = await api.post(`/contracts/${id}/pdf`, {}, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${rec.title || "Vertrag"}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("PDF erstellt");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const markSigned = async () => {
    const ok = await confirmDialog({
      title: "Als unterzeichnet markieren?",
      description: "Der Vertrag ist danach nicht mehr frei bearbeitbar; nur noch Aktivierung möglich.",
      confirmLabel: "Unterzeichnet", tone: "warn",
    });
    if (!ok) return;
    setBusy(true);
    try {
      await api.post(`/contracts/${id}/mark-signed`, {
        signature_place: signPlace || null,
        signature_date: signDate || null,
      });
      toast.success("Als unterzeichnet gespeichert");
      onChanged();
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const activate = async () => {
    const targetLabel = km.target;
    const ok = await confirmDialog({
      title: `${targetLabel} jetzt anlegen?`,
      description: `Aus dem Vertrag wird ein ${targetLabel} im System erstellt. Diese Aktion ist unumkehrbar.`,
      confirmLabel: `${targetLabel} anlegen`, tone: "success",
    });
    if (!ok) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/contracts/${id}/activate`);
      toast.success(`${targetLabel} angelegt`);
      if (data.tenancy_id) {
        toast.info(`Mietverhältnis-ID: ${data.tenancy_id}`);
      }
      onChanged();
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent
        className="max-w-[95vw] w-[1400px] max-h-[95vh] overflow-hidden flex flex-col p-4"
        data-testid="contract-detail"
      >
        <DialogHeader>
          <div className="flex items-center gap-2 flex-wrap">
            <DialogTitle>{rec.title}</DialogTitle>
            <span className={`px-2 py-0.5 rounded-sm text-[11px] font-medium ${sm.tone}`}>{sm.label}</span>
            <span className="text-xs text-slate-500">{km.label}</span>
          </div>
          <DialogDescription>
            {rec.contract_number ? `${rec.contract_number} · ` : ""}
            {rec.property_name ? `${rec.property_name} · ` : ""}
            {(rec.tenant_names || []).join(", ")}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-hidden grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-3 min-h-0">
          {/* LEFT: Editor + Signature + Meta */}
          <div className="overflow-y-auto pr-2 space-y-4 min-h-0">
            {/* Body editor */}
            <div>
              <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
                <Label>Vertragstext (HTML)</Label>
                <div className="flex gap-2">
                  <Button
                    variant="outline" size="sm"
                    onClick={() => setShowClauses(true)} disabled={!editable}
                    data-testid="open-clauses"
                    className="gap-1"
                  >
                    <BookOpen size={13} /> Klauseln
                  </Button>
                  <Button
                    variant="outline" size="sm"
                    onClick={regenerate} disabled={!rec.template_id || !editable}
                    data-testid="contract-regenerate"
                  >
                    Aus Vorlage
                  </Button>
                </div>
              </div>
              <Textarea
                ref={bodyRef}
                rows={16}
                value={body}
                disabled={!editable}
                onChange={(e) => { setBody(e.target.value); setDirty(true); }}
                className="font-mono text-xs"
                data-testid="contract-body"
              />
            </div>

            {/* Signature blocks */}
            <SignatoryList
              signatories={rec.signatories || []}
              editable={editable}
              onChange={(sigs) => { setRec({ ...rec, signatories: sigs }); setDirty(true); }}
            />

            {/* Signature meta */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Unterschriftsort (Sammel)</Label>
                <Input value={signPlace} disabled={!editable}
                       onChange={(e) => { setSignPlace(e.target.value); setDirty(true); }}
                       data-testid="contract-sign-place" />
              </div>
              <div>
                <Label>Unterschriftsdatum (Sammel)</Label>
                <Input type="date" value={signDate?.slice(0, 10) || ""} disabled={!editable}
                       onChange={(e) => { setSignDate(e.target.value); setDirty(true); }}
                       data-testid="contract-sign-date" />
              </div>
            </div>

            {/* Result info */}
            {rec.created_tenancy_id && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-3 text-sm text-emerald-900">
                <CheckCircle weight="fill" className="inline mr-1" size={14} />
                Mietverhältnis wurde angelegt: <b>{rec.created_tenancy_id.slice(0, 8)}…</b>
              </div>
            )}
            {rec.created_management_contract_id && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-3 text-sm text-emerald-900">
                <CheckCircle weight="fill" className="inline mr-1" size={14} />
                Verwaltervertrag wurde angelegt: <b>{rec.created_management_contract_id.slice(0, 8)}…</b>
              </div>
            )}
          </div>

          {/* RIGHT: PDF Preview */}
          <ContractPdfPreview
            contractId={id}
            body={body}
            title={rec.title}
            signatories={rec.signatories || []}
            signaturePlace={signPlace}
            signatureDate={signDate}
            dirty={dirty}
          />
        </div>

        <DialogFooter className="flex-wrap gap-2 justify-between border-t pt-3 mt-2">
          <div className="flex gap-2">
            <Button variant="outline" onClick={downloadPdf} className="gap-2" data-testid="contract-pdf">
              <FilePdf size={14} /> PDF
            </Button>
            {editable && (
              <Button onClick={save} disabled={!dirty || busy} className="gap-2 bg-slate-900 hover:bg-slate-800"
                      data-testid="contract-save">
                {busy ? "Speichern…" : "Speichern"}
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            {rec.status === "draft" && (
              <Button onClick={markSigned} disabled={busy} className="gap-2 bg-amber-600 hover:bg-amber-700"
                      data-testid="contract-mark-signed">
                <Signature size={14} /> Als unterzeichnet
              </Button>
            )}
            {rec.status === "signed" && (
              <Button onClick={activate} disabled={busy} className="gap-2 bg-emerald-600 hover:bg-emerald-700"
                      data-testid="contract-activate">
                <Lightning size={14} /> {km.target} anlegen
              </Button>
            )}
            <Button variant="outline" onClick={onClose}>Schließen</Button>
          </div>
        </DialogFooter>
      </DialogContent>

      {showClauses && (
        <ClausePickerSheet
          kind={rec.kind}
          onClose={() => setShowClauses(false)}
          onInsert={(html) => insertClause(html)}
        />
      )}
    </Dialog>
  );
}

function SignatoryList({ signatories, editable, onChange }) {
  const addBlock = () => onChange([...signatories, { role: "", name: "", subline: "" }]);
  const update = (i, patch) => onChange(signatories.map((s, idx) => idx === i ? { ...s, ...patch } : s));
  const remove = (i) => onChange(signatories.filter((_, idx) => idx !== i));

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <Label>Unterschriftsblöcke ({signatories.length})</Label>
        {editable && (
          <Button variant="outline" size="sm" onClick={addBlock} className="gap-1" data-testid="add-signatory">
            <Plus size={13} /> Block hinzufügen
          </Button>
        )}
      </div>
      <div className="space-y-2">
        {signatories.map((sig, i) => (
          <div key={i} className="border border-slate-200 rounded-sm p-2 bg-slate-50/40 grid grid-cols-[1fr_1fr_1fr_auto] gap-2 items-end">
            <div>
              <Label className="text-[10px]">Rolle</Label>
              <Input value={sig.role || ""} disabled={!editable}
                     onChange={(e) => update(i, { role: e.target.value })}
                     placeholder="Vermieter / Mieter / Verwalter"
                     data-testid={`sig-role-${i}`}
                     className="h-8 text-xs" />
            </div>
            <div>
              <Label className="text-[10px]">Name</Label>
              <Input value={sig.name || ""} disabled={!editable}
                     onChange={(e) => update(i, { name: e.target.value })}
                     data-testid={`sig-name-${i}`}
                     className="h-8 text-xs" />
            </div>
            <div>
              <Label className="text-[10px]">Zusatzzeile (Firma / Adresse)</Label>
              <Input value={sig.subline || ""} disabled={!editable}
                     onChange={(e) => update(i, { subline: e.target.value })}
                     placeholder="Auto aus Kontakt"
                     className="h-8 text-xs" />
            </div>
            <button onClick={() => remove(i)} disabled={!editable}
                    className="p-1 text-slate-400 hover:text-rose-600 disabled:opacity-30"
                    data-testid={`sig-remove-${i}`}>
              <X size={14} />
            </button>
          </div>
        ))}
        {signatories.length === 0 && (
          <div className="text-xs text-slate-400 italic border border-dashed border-slate-200 rounded-sm p-2 text-center">
            Noch keine Unterschriftsblöcke — werden bei Anlage automatisch aus Vermieter/Mieter erzeugt.
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// PDF Live-Vorschau — rechte Seite des Vertrags-Dialogs.
// Rendert das PDF über /contracts/{id}/preview mit dem ungespeicherten Body.
// Auto-Refresh 900 ms nach letzter Änderung (debounced).
// ---------------------------------------------------------------------------
function ContractPdfPreview({ contractId, body, title, signatories, signaturePlace, signatureDate, dirty }) {
  const [previewUrl, setPreviewUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const reqIdRef = useRef(0);

  const refresh = async () => {
    const myReq = ++reqIdRef.current;
    setLoading(true);
    setError(null);
    try {
      const resp = await api.post(
        `/contracts/${contractId}/preview`,
        {
          body_html: body,
          title,
          signatories,
          signature_place: signaturePlace || null,
          signature_date: signatureDate || null,
        },
        { responseType: "arraybuffer" },
      );
      if (myReq !== reqIdRef.current) return;
      // Data-URL statt Blob-URL: keine Revocation-Rennen, StrictMode-safe.
      const b64 = btoa(
        new Uint8Array(resp.data)
          .reduce((acc, byte) => acc + String.fromCharCode(byte), "")
      );
      setPreviewUrl(`data:application/pdf;base64,${b64}`);
    } catch (e) {
      if (myReq === reqIdRef.current) setError(formatApiError(e));
    } finally {
      if (myReq === reqIdRef.current) setLoading(false);
    }
  };

  // Initial + debounced auto-refresh on body/title/signatories change
  useEffect(() => {
    const t = setTimeout(() => { refresh(); }, previewUrl ? 900 : 100);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [body, title, JSON.stringify(signatories), signaturePlace, signatureDate]);

  useEffect(() => {
    return () => {
      // Blob-URLs werden vom Browser bei Seiten-Reload/Tab-Close automatisch freigegeben.
      // Manuelles Revoke unterlassen wir absichtlich, weil StrictMode-Double-Mounts sonst
      // die iframe-Ladung abbrechen (ERR_ABORTED → blanke Vorschau).
    };
  }, []);

  return (
    <div className="border border-slate-200 rounded-md bg-slate-50 flex flex-col min-h-0" data-testid="contract-preview">
      <div className="flex items-center justify-between px-2 py-1.5 border-b border-slate-200 bg-white text-xs text-slate-600">
        <div className="flex items-center gap-1.5">
          <FilePdf size={14} className="text-emerald-600" />
          <span className="font-medium">PDF-Vorschau</span>
          {loading && <span className="text-slate-400 animate-pulse">· lädt…</span>}
          {!loading && dirty && (
            <span className="text-amber-600">· ungespeicherte Änderungen</span>
          )}
        </div>
        <button
          onClick={refresh}
          className="p-1 rounded hover:bg-slate-100 text-slate-500"
          title="Vorschau aktualisieren"
          data-testid="preview-refresh"
        >
          <ArrowsClockwise size={14} className={loading ? "animate-spin" : ""} />
        </button>
      </div>
      <div className="flex-1 overflow-hidden">
        {error && (
          <div className="p-3 text-xs text-rose-700 bg-rose-50 border-b border-rose-200">
            {error}
          </div>
        )}
        {previewUrl ? (
          <object
            data={previewUrl}
            type="application/pdf"
            className="w-full h-full bg-white"
            aria-label="Vertragsvorschau"
            data-testid="preview-iframe"
          >
            <iframe
              src={previewUrl}
              title="Vertragsvorschau"
              className="w-full h-full border-0 bg-white"
            />
          </object>
        ) : (
          <div className="h-full flex items-center justify-center text-sm text-slate-400">
            Wird geladen…
          </div>
        )}
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Klauseln-Bibliothek — Side-Sheet mit Kategorien-Filter + Klick-Einfügen.
// ---------------------------------------------------------------------------
function ClausePickerSheet({ kind, onClose, onInsert }) {
  const [clauses, setClauses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [category, setCategory] = useState(null);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);   // { id?, title, category, body_html, tags, contract_kinds }
  const confirmDialog = useConfirm();

  const reload = async () => {
    setLoading(true);
    try {
      // Kind-Filter absichtlich nicht auf den Server durchreichen, damit auch
      // universelle Klauseln oder solche anderer Typen sichtbar sind (der Nutzer
      // entscheidet selbst, was er einfügt).
      const [c, cats] = await Promise.all([
        api.get("/contract-clauses"),
        api.get("/contract-clauses/categories"),
      ]);
      setClauses(c.data || []);
      setCategories((cats.data || []).filter((x) => (x.count || 0) > 0));
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, []);

  const filtered = useMemo(() => {
    const q = (search || "").toLowerCase().trim();
    return clauses.filter((c) => {
      if (category && c.category !== category) return false;
      // Weicher Kind-Filter: Klauseln mit einer Beschränkung, die den aktuellen
      // Vertragstyp NICHT enthalten, werden nach unten sortiert (nicht ausgeblendet).
      if (!q) return true;
      const hay = [c.title, c.category, (c.tags || []).join(" ")].join(" ").toLowerCase();
      return hay.includes(q);
    }).sort((a, b) => {
      const aMatches = !a.contract_kinds?.length || a.contract_kinds.includes(kind);
      const bMatches = !b.contract_kinds?.length || b.contract_kinds.includes(kind);
      if (aMatches !== bMatches) return aMatches ? -1 : 1;
      return 0;
    });
  }, [clauses, category, search, kind]);

  const seed = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/contract-clauses/seed-defaults");
      toast.success(`${data.created} Standard-Klauseln hinzugefügt`);
      reload();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const insert = async (c) => {
    onInsert(c.body_html);
    toast.success(`„${c.title}" eingefügt`);
    // Usage-count nachziehen (fire-and-forget)
    try { await api.post(`/contract-clauses/${c.id}/mark-used`); } catch { /* non-blocking */ }
  };

  const del = async (c) => {
    const ok = await confirmDialog({
      title: "Klausel löschen?",
      description: `„${c.title}" wird endgültig entfernt.`,
      confirmLabel: "Löschen", tone: "danger",
    });
    if (!ok) return;
    try {
      await api.delete(`/contract-clauses/${c.id}`);
      toast.success("Gelöscht");
      reload();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const saveClause = async () => {
    if (!editing?.title || !editing?.body_html) {
      toast.error("Titel und Text sind Pflicht.");
      return;
    }
    setBusy(true);
    try {
      if (editing.id) {
        await api.patch(`/contract-clauses/${editing.id}`, editing);
      } else {
        await api.post("/contract-clauses", editing);
      }
      toast.success("Gespeichert");
      setEditing(null);
      reload();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Sheet open onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="!w-[560px] !max-w-[95vw] p-0 overflow-hidden flex flex-col"
                    data-testid="clauses-sheet">
        <SheetHeader className="p-4 pb-2 border-b">
          <SheetTitle className="flex items-center gap-2">
            <BookOpen size={18} className="text-emerald-600" />
            Vertragsklauseln
          </SheetTitle>
          <div className="text-xs text-slate-500">
            Klick auf eine Klausel fügt sie an der Cursor-Position im Editor ein.
          </div>
        </SheetHeader>

        {editing ? (
          <ClauseEditor
            editing={editing} setEditing={setEditing}
            onSave={saveClause} onCancel={() => setEditing(null)} busy={busy}
          />
        ) : (
          <>
            <div className="p-3 border-b bg-white space-y-2">
              <div className="flex items-center gap-2">
                <div className="relative flex-1">
                  <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    value={search} onChange={(e) => setSearch(e.target.value)}
                    placeholder="Suchen (Titel, Tag …)"
                    className="pl-8 h-8 text-xs"
                    data-testid="clauses-search"
                  />
                </div>
                <Button
                  size="sm" variant="outline" className="gap-1"
                  onClick={() => setEditing({ title: "", category: "Allgemein", body_html: "", tags: [], contract_kinds: [] })}
                  data-testid="add-clause-btn"
                >
                  <Plus size={13} /> Neu
                </Button>
              </div>
              <div className="flex items-center gap-1 flex-wrap">
                <button
                  onClick={() => setCategory(null)}
                  className={`text-[11px] px-2 py-0.5 rounded-full border ${
                    !category ? "bg-emerald-600 border-emerald-600 text-white" : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"
                  }`}
                >
                  Alle ({clauses.length})
                </button>
                {categories.map((c) => (
                  <button
                    key={c.name}
                    onClick={() => setCategory(c.name === category ? null : c.name)}
                    className={`text-[11px] px-2 py-0.5 rounded-full border ${
                      category === c.name ? "bg-emerald-600 border-emerald-600 text-white" : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"
                    }`}
                  >
                    {c.name} <span className="opacity-70">{c.count}</span>
                  </button>
                ))}
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-2 space-y-2">
              {loading && <div className="text-center text-slate-500 py-10">Lade…</div>}
              {!loading && clauses.length === 0 && (
                <div className="text-center text-slate-500 py-10 space-y-3">
                  <BookOpen size={32} className="mx-auto text-slate-300" />
                  <div>Noch keine Klauseln vorhanden.</div>
                  <Button size="sm" onClick={seed} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700">
                    12 Standard-Klauseln anlegen
                  </Button>
                </div>
              )}
              {!loading && filtered.map((c) => (
                <div key={c.id}
                     className="group border border-slate-200 rounded-sm p-3 bg-white hover:border-emerald-400 hover:shadow-sm transition-all cursor-pointer"
                     onClick={() => insert(c)}
                     data-testid={`clause-${c.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        {c.is_favorite && <Star weight="fill" size={12} className="text-amber-500 flex-shrink-0" />}
                        <div className="font-medium text-sm text-slate-900 truncate">{c.title}</div>
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                          {c.category}
                        </span>
                        {c.usage_count > 0 && (
                          <span className="text-[10px] text-slate-400">
                            {c.usage_count}× genutzt
                          </span>
                        )}
                      </div>
                      <div
                        className="text-xs text-slate-500 mt-1 line-clamp-2"
                        dangerouslySetInnerHTML={{
                          __html: (c.body_html || "").replace(/<[^>]+>/g, " ").slice(0, 160),
                        }}
                      />
                    </div>
                    <div className="opacity-0 group-hover:opacity-100 flex flex-col gap-1 flex-shrink-0">
                      <button
                        onClick={(e) => { e.stopPropagation(); setEditing({ ...c }); }}
                        className="p-1 text-slate-400 hover:text-slate-700"
                        title="Bearbeiten"
                        data-testid={`clause-edit-${c.id}`}
                      >
                        <PencilSimple size={13} />
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); del(c); }}
                        className="p-1 text-slate-400 hover:text-rose-600"
                        title="Löschen"
                        data-testid={`clause-delete-${c.id}`}
                      >
                        <Trash size={13} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
              {!loading && clauses.length > 0 && filtered.length === 0 && (
                <div className="text-center text-slate-400 py-10 text-sm italic">
                  Keine Treffer für &quot;{search}&quot;{category ? ` in Kategorie „${category}"` : ""}.
                </div>
              )}
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

function ClauseEditor({ editing, setEditing, onSave, onCancel, busy }) {
  const setField = (patch) => setEditing((c) => ({ ...c, ...patch }));
  return (
    <div className="flex-1 overflow-y-auto p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div className="font-medium text-sm text-slate-900">
          {editing.id ? "Klausel bearbeiten" : "Neue Klausel"}
        </div>
        <button onClick={onCancel} className="p-1 text-slate-400 hover:text-slate-700">
          <X size={16} />
        </button>
      </div>
      <div>
        <Label className="text-xs">Titel*</Label>
        <Input
          value={editing.title || ""} onChange={(e) => setField({ title: e.target.value })}
          data-testid="clause-title"
        />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-xs">Kategorie</Label>
          <Input
            value={editing.category || ""} onChange={(e) => setField({ category: e.target.value })}
            placeholder="Allgemein"
          />
        </div>
        <div>
          <Label className="text-xs">Tags (kommagetrennt)</Label>
          <Input
            value={(editing.tags || []).join(", ")}
            onChange={(e) => setField({ tags: e.target.value.split(",").map((t) => t.trim()).filter(Boolean) })}
          />
        </div>
      </div>
      <div>
        <Label className="text-xs">Vertragstypen (kommagetrennt, leer = alle)</Label>
        <Input
          value={(editing.contract_kinds || []).join(", ")}
          onChange={(e) => setField({ contract_kinds: e.target.value.split(",").map((t) => t.trim()).filter(Boolean) })}
          placeholder="wohnraum, gewerbe, verwalter…"
        />
      </div>
      <div>
        <Label className="text-xs">Text (HTML)*</Label>
        <Textarea
          rows={10} className="font-mono text-xs"
          value={editing.body_html || ""}
          onChange={(e) => setField({ body_html: e.target.value })}
          data-testid="clause-body"
          placeholder="<h3>§ Kündigung</h3><p>…</p>"
        />
      </div>
      <label className="flex items-center gap-2 text-sm text-slate-700">
        <input type="checkbox" checked={!!editing.is_favorite}
               onChange={(e) => setField({ is_favorite: e.target.checked })}
               className="w-4 h-4 accent-amber-500" />
        Als Favorit markieren (immer oben in der Liste)
      </label>
      <div className="flex justify-end gap-2 pt-2 border-t">
        <Button variant="outline" onClick={onCancel}>Abbrechen</Button>
        <Button onClick={onSave} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="clause-save">
          {busy ? "Speichern…" : "Speichern"}
        </Button>
      </div>
    </div>
  );
}

